// ./src/hooks/useGuestGroups.ts
//
// Loads a set's values (active + disabled), by display order. Skips loading until
// a setId is provided (the values drawer only mounts for a chosen set).

import { useCallback, useEffect, useRef, useState } from "react";
import { OpResult, OP_RESULT_CODES } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";

import { GuestGroupService, GuestGroup } from "@/utils/api";
import { useRefreshOnWriteTimeout } from "./useRefreshOnWriteTimeout";

export interface UseGuestGroupsReturn {
  groups: GuestGroup[];
  loading: boolean;
  /**
   * ⚠ Whether the FIRST read for this target has COME BACK — not the same as "not loading". The
   * read starts in an effect, which runs after the first paint, so a consumer branching on
   * `loading && rows.length === 0` paints its empty state for one frame every time it opens.
   * ⚠ It goes back to false when the target changes: that is a different first read.
   */
  isLoaded: boolean;
  error: OpResult | null;
  reload: () => Promise<void>;
}

export const useGuestGroups = (guestGroupSetId?: string | null): UseGuestGroupsReturn => {
  const [groups, setGroups] = useState<GuestGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<OpResult | null>(null);
  const callIdRef = useRef(0);

  const load = useCallback(async () => {
    if (!guestGroupSetId) {
      setGroups([]);
      return;
    }
    const myCall = ++callIdRef.current;
    setLoading(true);
    setError(null);

    const result = await GuestGroupService.list(guestGroupSetId, [STATUSES.ACTIVE, STATUSES.DISABLED]);

    if (myCall !== callIdRef.current) return;
    setLoading(false);
    // ⚠ On both paths — a read that FAILED has still come back, and leaving the flag false would
    // hold the skeleton on screen for ever instead of showing what went wrong.
    setIsLoaded(true);

    if (result.code === OP_RESULT_CODES.OK && Array.isArray(result.data)) {
      setGroups(result.data as GuestGroup[]);
    } else {
      setError(result);
      setGroups([]);
    }
  }, [guestGroupSetId]);

  // ROADMAP #131 — a save that timed out may still have landed: re-read what is shown.
  useRefreshOnWriteTimeout(load, isLoaded);

  useEffect(() => {
    // ⚠ A DIFFERENT TARGET IS A DIFFERENT FIRST READ (`load` changes identity only when the target
    // does). Without this the consumer would show the PREVIOUS target's rows as though they were
    // this one's while the new read is out.
    setIsLoaded(false);
    load();
  }, [load]);

  return { groups, loading, isLoaded, error, reload: load };
};
